import { Alert, Button, CircularProgress, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { apiClient } from '../../services/api';
import { BaseModal } from '../common/UI/BaseModal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

const UpdateSharepointSecretModal: React.FC<Props> = ({ open, onClose }) => {
  const { account, refreshUserDetails } = useAccount();
  const [newSecret, setNewSecret] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleUpdate = async () => {
    if (!newSecret.trim()) {
      setError('Client Secret cannot be empty.');
      return;
    }

    if (!newExpiryDate) {
      setError('Client Secret Expiration Date cannot be empty.');
      return;
    }

    if (!account?.sharepointConfig) {
      setError('Current SharePoint configuration is missing.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Build the full credentials object required by the backend
      const credentials = {
        tenantId: account.sharepointConfig.tenantId as string,
        clientId: account.sharepointConfig.clientId as string,
        clientSecret: newSecret.trim(),
        secretExpiryDate: newExpiryDate,
        siteUrl: account.sharepointConfig.siteUrl as string,
        siteId: account.sharepointConfig.siteId as string,
        driveId: account.sharepointConfig.driveId as string,
      };

      // 1. Save the new credentials
      // The backend detects previousStorageType === 'sharepoint' and skips the file transfer.
      await apiClient.post('/setSharepointCredentials', credentials);

      // 2. Verify the connection
      const verificationResult = await apiClient.post(
        '/checkSharepointConnection',
        {},
        { timeout: 60000 }
      );

      if (verificationResult.data.success) {
        setSuccess('Client Secret updated and connection verified successfully.');
        await refreshUserDetails();
        setNewSecret('');
        setTimeout(() => {
          onClose();
          setSuccess(null);
        }, 2000);
      } else {
        setError(verificationResult.data.message || 'Verification failed with the new secret.');
      }
    } catch (err: unknown) {
      const apiError = err as ApiError;
      setError(
        apiError.response?.data?.message || apiError.message || 'An unexpected error occurred.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setNewSecret('');
      setNewExpiryDate('');
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      title="Update SharePoint Client Secret"
      actions={
        <>
          <Button onClick={handleClose} disabled={isLoading} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            color="primary"
            disabled={isLoading || !newSecret.trim() || !newExpiryDate || !!success}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Updating...' : 'Update & Verify'}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
        If your Azure AD Client Secret has expired, you can generate a new one in the Azure Portal
        and paste it here to restore the connection without needing to migrate files. For detailed
        instructions on how to generate a new Client Secret, please see the{' '}
        <a href="/sharepoint-guide" target="_blank" rel="noopener noreferrer">
          SharePoint Setup Guide
        </a>
        .
      </Typography>

      <TextField
        autoFocus
        margin="dense"
        id="clientSecret"
        label="New Client Secret"
        type="password"
        fullWidth
        variant="outlined"
        value={newSecret}
        onChange={(e) => setNewSecret(e.target.value)}
        disabled={isLoading || !!success}
      />

      <TextField
        margin="dense"
        id="secretExpiryDate"
        label="New Client Secret Expiration Date"
        type="date"
        fullWidth
        variant="outlined"
        value={newExpiryDate}
        onChange={(e) => setNewExpiryDate(e.target.value)}
        disabled={isLoading || !!success}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ mt: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </BaseModal>
  );
};

export default UpdateSharepointSecretModal;
